/**
 * Backtest Types - Pure Zod schemas
 *
 * Converted from Drizzle ORM schema (backtrader-frontend/shared/schema.ts).
 * All Drizzle/pg-core dependencies removed. Tables converted to Zod object schemas.
 * userId / foreign key references removed per instructions.
 */
import { z } from 'zod'

// ================================================================================
// GRAPH STRUCTURE SCHEMAS (used by nodes/edges fields in core entity schemas)
// ================================================================================

export interface GraphNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, unknown>
  [key: string]: unknown
}

export interface GraphEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  [key: string]: unknown
}

export const graphNodeSchema: z.ZodType<GraphNode> = z
  .object({
    id: z.string(),
    type: z.string(),
    // Attachment pseudo-nodes (e.g. debugChart) were persisted without a
    // position by older builds. `z.array(graphNodeSchema)` is all-or-nothing,
    // so requiring position let one such node reject the ENTIRE strategy and
    // hang the editor on "Loading Strategy…". Default instead of reject.
    position: z.object({ x: z.number(), y: z.number() }).default({ x: 0, y: 0 }),
    data: z.record(z.unknown()),
  })
  .passthrough() as z.ZodType<GraphNode>

export const graphEdgeSchema: z.ZodType<GraphEdge> = z
  .object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    sourceHandle: z.string().nullable().optional(),
    targetHandle: z.string().nullable().optional(),
  })
  .passthrough() as z.ZodType<GraphEdge>

// ================================================================================
// CORE ENTITY SCHEMAS (converted from Drizzle pgTable definitions)
// ================================================================================

/**
 * A strategy written as EdgeScript (phase 3). `source` is the truth; `nodes` and
 * `edges` are the compile artifact. `compiledHash` is the hash of the source that
 * produced the stored graph, so a source saved with errors is visibly stale and Run
 * refuses it. Top-level on purpose: `uiState` is rewritten whole on every layout
 * save and would erase it.
 */
export const strategyScriptSchema = z.object({
  source: z.string(),
  compiledHash: z.string(),
})
export type StrategyScript = z.infer<typeof strategyScriptSchema>

export const strategySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  /** Monotonic revision for atomic graph writes; legacy records read as zero. */
  graphRevision: z.number().int().nonnegative().optional(),
  /** sha256 of the stored nodes, edges and script, written with every graph
   *  write (edge-user-api graph_hash). Absent on records not written since,
   *  and when the server could not tell the stored script. */
  graphHash: z.string().nullable().optional(),

  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  script: strategyScriptSchema.optional(),
  championResultId: z.string().optional(),
  tags: z.array(z.string()).default([]),
  // Denormalized champion metrics (for dashboard display)
  totalReturn: z.number().optional(),
  maxDrawdown: z.number().optional(),
  sharpeRatio: z.number().optional(),
  winRate: z.number().optional(),
  totalTrades: z.number().optional(),
  profitFactor: z.number().optional(),
  ticker: z.string().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  createdAt: z.string().optional(), // ISO date
  updatedAt: z.string().optional(), // ISO date
  // Fields are optional: the list endpoint parses every strategy, and a
  // stored uiState that predates a field must not blank the whole page.
  uiState: z
    .object({
      activePanes: z.array(z.string()).optional(),
      dividerPosition: z.number().optional(),
      // Which editor the strategy opens in. Absent until the person chose
      // one on the starting screen (or the graph made the choice for them).
      // 'code' is the EdgeScript editor (phase 3, dev-gated).
      editorMode: z.enum(['form', 'canvas', 'code']).optional(),
    })
    .optional(),
})

// The strategies LIST endpoint returns lightweight metadata only — the heavy
// `nodes`/`edges` graphs are omitted server-side and fetched on demand via
// getStrategy(id). This is a DISTINCT type (not Strategy with optional graph)
// on purpose: the type system then flags any list consumer that needs the
// graph, forcing it to fetch the full strategy by id rather than silently
// reading an absent graph.
export const strategyListItemSchema = strategySchema.omit({ nodes: true, edges: true })
export type StrategyListItem = z.infer<typeof strategyListItemSchema>

export const portfolioSchema = z
  .object({
    initial_cash: z.number().nullable().optional(),
    final_portfolio_value: z.number().nullable().optional(),
    final_cash: z.number().nullable().optional(),
    total_pnl: z.number().nullable().optional(),
    total_return: z.number().nullable().optional(),
    commission_paid: z.number().nullable().optional(),
    locate_fees_paid: z.number().nullable().optional(),
    slippage_cost_total: z.number().nullable().optional(),
    returns: z
      .object({
        rtot: z.number().nullable().optional(),
        ravg: z.number().nullable().optional(),
        rnorm: z.number().nullable().optional(),
        rnorm100: z.number().nullable().optional(),
      })
      .nullable()
      .optional(),
    drawdown: z
      .object({
        len: z.number().nullable().optional(),
        drawdown: z.number().nullable().optional(),
        moneydown: z.number().nullable().optional(),
        max: z
          .object({
            len: z.number().nullable().optional(),
            drawdown: z.number().nullable().optional(),
            moneydown: z.number().nullable().optional(),
          })
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
    sharpe_ratio: z.number().nullable().optional(),
    vwr: z.number().nullable().optional(),
    sqn: z.number().nullable().optional(),
    sqn_trades: z.number().nullable().optional(),
    calmar_ratio: z.number().nullable().optional(),
  })
  .passthrough()

export const backtestResultSchema = z.object({
  id: z.string(),
  strategyId: z.string().optional(),
  // Summary metrics
  totalReturn: z.number().nullable().optional(),
  maxDrawdown: z.number().nullable().optional(),
  sharpeRatio: z.number().nullable().optional(),
  winRate: z.number().nullable().optional(),
  totalTrades: z.number().nullable().optional(),
  profitFactor: z.number().nullable().optional(),
  // Legacy catch-all
  results: z.record(z.unknown()).optional(),
  // Graph snapshot
  strategyName: z.string().optional(),
  nodes: z.array(graphNodeSchema).optional(),
  edges: z.array(graphEdgeSchema).optional(),
  filters: z.record(z.unknown()).optional(),
  parameters: z.record(z.unknown()).optional(),
  // S3 URIs
  transactionsUri: z.string().optional(),
  transactionsCsvUri: z.string().optional(),
  timeReturnsUri: z.string().optional(),
  debugFileUri: z.string().optional(),
  ohlcvUri: z.string().optional(),
  logFileUri: z.string().optional(),
  // Lambda metadata
  executionId: z.string().optional(),
  ticker: z.string().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  initialCash: z.number().nullable().optional(),
  executionTime: z.number().nullable().optional(),
  // Full portfolio
  portfolio: portfolioSchema.nullable().optional(),
  createdAt: z.string().optional(),
})

export const strategyVersionSchema = z.object({
  id: z.string(),
  strategyId: z.string().optional(),
  // Both `versionNumber` and `name` are intentionally permissive because the
  // backend may write skeletal/unnamed rows on operations other than
  // "Save as version…" (e.g. every PATCH /strategies/:id auto-save). A strict
  // schema causes Zod's array.parse() to reject the ENTIRE list when a
  // single row is missing these fields — silent failure mode that
  // manifests as "saved versions don't show up." See useStrategyVersions
  // for the error-logging path.
  versionNumber: z.number().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  /** The source at snapshot time, for strategies written as code. */
  script: strategyScriptSchema.optional(),
  /** sha256 of the stored nodes, edges and script. Absent on rows written
   *  before the field; a run reuses the newest version with its graph's hash. */
  graphHash: z.string().optional(),
  /** 'run' when a batch run recorded this version; absent otherwise. A string,
   *  not an enum, so a future source never fails the whole list parse. */
  source: z.string().nullable().optional(),
  createdAt: z.string().optional(),
})

export const strategyVariantSchema = z.object({
  id: z.string(),
  strategyId: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  isChampion: z.boolean().default(false),
  winRate: z.number().nullable().optional(),
  totalReturn: z.number().nullable().optional(),
  maxDrawdown: z.number().nullable().optional(),
  sharpeRatio: z.number().nullable().optional(),
  totalTrades: z.number().nullable().optional(),
  profitFactor: z.number().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const snippetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  nodes: z.array(graphNodeSchema),
  edges: z.array(graphEdgeSchema),
  nodeCount: z.number(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const sentimentEnum = z.enum(['bullish', 'neutral', 'bearish'])

export const journalEntrySchema = z.object({
  id: z.string(),
  strategyId: z.string(),
  resultId: z.string(),
  tradeIndex: z.number(),
  notes: z.string().nullable().optional(),
  sentiment: sentimentEnum.nullable().optional(),
  tags: z.array(z.string()).default([]),
  lessonsLearned: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

// ── Filter Presets ──

export const filterPresetSchema = z.object({
  filterId: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  source: z.enum(['manual', 'batch_execution']),
  inputSource: z.enum(['filters', 'csv']),
  filters: z.record(z.unknown()).nullable().optional(),
  filtersVersion: z.string().default('1.0'),
  csvFileName: z.string().nullable().optional(),
  inputCount: z.number(),
  inputs: z
    .array(z.object({ ticker: z.string(), target_date: z.string() }))
    .nullable()
    .optional(),
  universeName: z.string().nullable().optional(),
  usageCount: z.number(),
  lastUsedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const filterPresetSummarySchema = z.object({
  filterId: z.string(),
  name: z.string(),
  inputSource: z.enum(['filters', 'csv']),
  inputCount: z.number(),
})

// ── Trade Return Histogram ──

export const tradeReturnHistogramBinSchema = z
  .object({
    start: z.number(),
    end: z.number(),
    count: z.number(),
  })
  .passthrough()

export const tradeReturnHistogramSchema = z
  .object({
    scope: z.string().nullable().optional(),
    binWidthPct: z.number().nullable().optional(),
    bins: z.array(tradeReturnHistogramBinSchema).default([]),
  })
  .passthrough()

export type TradeReturnHistogram = z.infer<typeof tradeReturnHistogramSchema>

// ── Strategy Metrics (rollup endpoint) ──

export const strategyMetricsSummarySchema = z
  .object({
    totalPnl: z.number().nullable().optional(),
    totalTrades: z.number().nullable().optional(),
    winRate: z.number().nullable().optional(),
    maxDrawdown: z.number().nullable().optional(),
    successfulTickers: z.number().nullable().optional(),
    failedTickers: z.number().nullable().optional(),
    totalTickers: z.number().nullable().optional(),
  })
  .passthrough()

export const strategyMetricsResultSchema = z
  .object({
    id: z.string().optional(),
    totalReturn: z.number().nullable().optional(),
    winRate: z.number().nullable().optional(),
    sharpeRatio: z.number().nullable().optional(),
    maxDrawdown: z.number().nullable().optional(),
    profitFactor: z.number().nullable().optional(),
    totalTrades: z.number().nullable().optional(),
    initialCash: z.number().nullable().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough()

export const strategyMetricsTickerSchema = z
  .object({
    ticker: z.string(),
    totalReturn: z.number().nullable().optional(),
    winRate: z.number().nullable().optional(),
    totalTrades: z.number().nullable().optional(),
    pnl: z.number().nullable().optional(),
    lastRunAt: z.string().nullable().optional(),
  })
  .passthrough()

export type StrategyMetricsTicker = z.infer<typeof strategyMetricsTickerSchema>

export const strategyMetricsSchema = z
  .object({
    strategyId: z.string(),
    updatedAt: z.string().nullable().optional(),
    summary: strategyMetricsSummarySchema.nullable().optional(),
    results: strategyMetricsResultSchema.array().default([]),
    tickers: strategyMetricsTickerSchema.array().default([]),
    tradeReturnHistogram: tradeReturnHistogramSchema.nullable().optional(),
  })
  .passthrough()

export type StrategyMetrics = z.infer<typeof strategyMetricsSchema>
export type StrategyMetricsResult = z.infer<typeof strategyMetricsResultSchema>

// ================================================================================
// CORE TYPE EXPORTS
// ================================================================================

export type Strategy = z.infer<typeof strategySchema>
export type BacktestResultRecord = z.infer<typeof backtestResultSchema>
export type StrategyVersion = z.infer<typeof strategyVersionSchema>
export type StrategyVariant = z.infer<typeof strategyVariantSchema>
export type Snippet = z.infer<typeof snippetSchema>
export type JournalEntry = z.infer<typeof journalEntrySchema>
export type FilterPreset = z.infer<typeof filterPresetSchema>
export type FilterPresetSummary = z.infer<typeof filterPresetSummarySchema>
